import React, { useEffect, useMemo, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
// Drag and Drop
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
// @mui
import { useTheme } from "@mui/material/styles";
import {
  Grid,
  Container,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Autocomplete,
  Chip,
  Divider,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Alert,
  Tooltip,
} from "@mui/material";
// components
import {
  Assistant,
  CameraEnhance,
  CurrencyRupeeOutlined,
  DocumentScanner,
  Person,
  SearchOutlined,
  Sync,
  TextFieldsOutlined,
  Visibility,
  Settings,
  Image,
  QuestionAnswer,
  Upload,
  Delete,
  Share,
  AutoStories,
  Psychology,
  RemoveRedEye,
  SmartToy,
  DragIndicator,
  InfoOutlined,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  useGetGoogleAnalyticsByRangeQuery,
  useGetGoogleAnalyticsQuery,
} from "../redux/services/googleAnalytics.service";
import { useGetOrganizationsQuery } from "../redux/services/organization.service";
import {
  useGetGoogleAnalyticsConfigsQuery,
  useBulkUpdateIndexMutation,
} from "../redux/services/googleAnalyticsConfig.service";
import { useGetOrganizationAnalyticsQuery } from "../redux/services/sponsorship.service";
import { selectAbility } from "../redux/slices/casl.slice";
import { selectUser } from "../redux/slices/auth.slice";
import {
  resetDashboardFilters,
  selectDashboardFilters,
  setDashboardFilters,
} from "../redux/slices/dashboard.slice";
import { useGetEventPlacesQuery } from "../redux/services/device.service";
// sections
import {
  LanguageChart,
  UsageChart,
  SubscribedChart,
  AppWidgetNew,
  SignUpUserChart,
} from "../sections/@dashboard/app";

// Sortable Card Component for drag and drop
function SortableCard({ id, card, value }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      ref={setNodeRef}
      style={style}
      data-testid="count-card"
    >
      <Box sx={{ position: "relative" }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 10,
            cursor: "grab",
            color: "text.secondary",
            opacity: 0.6,
            "&:hover": {
              opacity: 1,
            },
            "&:active": {
              cursor: "grabbing",
            },
          }}
        >
          <DragIndicator fontSize="small" />
        </Box>
        <AppWidgetNew
          title={card.title}
          total={value || 0}
          color={card.color}
          icon={card.icon}
          info={card.description}
          sx={{ height: "100%" }}
        />
      </Box>
    </Grid>
  );
}

export default function DashboardAppPage() {
  const theme = useTheme();

  const ability = useSelector(selectAbility);
  const user = useSelector(selectUser);
  const isOrgAdmin = user?.roles?.map((role) => role?.name)?.includes("organization_admin") || user?.roles?.map((role) => role?.name)?.includes("organization");
  const userOrgId = user?.organizationId || (typeof localStorage !== "undefined" && localStorage ? localStorage.getItem("orgId") : "") || "";

  const dashboardFilter = useSelector(selectDashboardFilters);
  const dispatch = useDispatch();

  const { register, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      userType: isOrgAdmin ? "vosapSubscription" : "all",
      usageCohort: "all",
      organizationId: isOrgAdmin ? userOrgId : "",
      distributorId: "",
      eventPlace: "",
      tag: "",
      deviceCreationDates: [],
      cameraType: "",
    },
  });

  const currentUsageCohort = watch("usageCohort") || "all";

  const [duration, setDuration] = useState("weekly");

  const [refresh, setRefresh] = useState(0);

  const [custom, setCustom] = useState(false);

  const hasInitializedCards = useRef(false);

  const [graphAllData, setGraphAllData] = useState({});

  const [graph1Data, setGraph1Data] = useState([]);

  const [graph2Data, setGraph2Data] = useState([]);

  const [graph3Data, setGraph3Data] = useState([]);

  const [graph4Data, setGraph4Data] = useState([]);

  const [graph5Data, setGraph5Data] = useState([]);

  const processedGraph1Data = useMemo(() => {
    if (!isOrgAdmin || !graph1Data || !graph1Data.length) return graph1Data;
    return graph1Data.map((item) => ({
      ...item,
      overview: {
        usersCount: item?.overview?.totalUsers ?? item?.overview?.totalActiveUsers ?? 0,
        sessionsCount: item?.overview?.totalEvents ?? 0,
      },
      cameraBasedFeatures: {
        cameraBasedFeatures: item?.cameraBasedFeatures?.total ?? item?.recognition?.totalImageCount ?? 0,
      },
      nonCameraBasedFeatures: {
        nonCameraBasedFeatures: item?.nonCameraBasedFeatures?.total ?? item?.featureUsage?.totalFeatureUsage ?? 0,
      },
      document: {
        readerPagesCount: item?.document?.readerPages ?? item?.document?.totalDocumentPageCount ?? 0,
        accessiblePagesCount: item?.document?.accessiblePages ?? 0,
      },
    }));
  }, [isOrgAdmin, graph1Data]);

  const [startDate, setStartDate] = useState(
    dayjs(new Date()?.toISOString()?.split("T")?.[0])?.subtract(6, "day"),
  );

  const [endDate, setEndDate] = useState(
    dayjs(new Date()?.toISOString()?.split("T")?.[0]),
  );

  const [customStartDate, setCustomStartDate] = useState(
    startDate?.format("YYYY-MM-DD") || "",
  );

  const [customEndDate, setCustomEndDate] = useState(
    endDate?.format("YYYY-MM-DD") || "",
  );

  // const [eevntPlaces, setEeventPlaces] = useState([]);
  const [selectedEventPlace, setSelectedEventPlace] = useState("");

  const [filteredTags, setFilteredTags] = useState([]); // State to store filtered tags

  // Card selection state
  const [cardSettingsOpen, setCardSettingsOpen] = useState(false);
  const MAX_CARDS = 12;

  // Fetch GA configs from API
  const { data: gaConfigsData, isSuccess: isGaConfigsSuccess } =
    useGetGoogleAnalyticsConfigsQuery(undefined);

  // Mutation for saving card order
  const [bulkUpdateIndex, { isLoading: isSavingOrder }] =
    useBulkUpdateIndexMutation();

  // Store GA configs for mapping keyName to id
  const [gaConfigsMap, setGaConfigsMap] = useState({});

  // Track if cards have been modified (for Save button)
  const [hasCardChanges, setHasCardChanges] = useState(false);

  // Curated human-friendly titles for cards (removing redundant "Total", improving clarity)
  const CARD_TITLES = {
    // Overview
    usersCount: "Total Users",
    totalUsers: "Total Users",
    activeUsers: "Active Users",
    periodActiveUsers: "Active Users",
    sessionsCount: "Sessions",
    totalSessions: "Sessions",
    sessions: "Sessions",

    // Recognition
    totalObjectCount: "Objects Detected",
    objectCount: "Objects Detected",
    totalCurrencyCount: "Currency Scans",
    currencyCount: "Currency Scans",
    totalWordCount: "Words Read",
    wordCount: "Words Read",
    totalImageCount: "Images Captured",
    imageCount: "Images Captured",

    // Modes
    totalTextModeCount: "Text Mode Usage",
    textModeCount: "Text Mode Usage",
    totalMiraCount: "MIRA Usage",
    miraCount: "MIRA Usage",
    totalTasksCount: "Tasks Completed",
    tasksCount: "Tasks Completed",
    totalTextToAudioCount: "Text to Audio",
    textToAudioCount: "Text to Audio",

    // AI Chat
    totalAiChatAskQuestionCount: "Questions Asked",
    aiChatAskQuestionCount: "Questions Asked",
    totalAiChatAnswerCount: "Answers Received",
    aiChatAnswerCount: "Answers Received",

    // Smart Eye
    totalSmartEyeAskQuestionCount: "Questions Asked",
    smartEyeAskQuestionCount: "Questions Asked",
    totalSmartEyeAnswerCount: "Answers Received",
    smartEyeAnswerCount: "Answers Received",
    totalSmartEyeCount: "Smart Eye Usage",
    smartEyeCount: "Smart Eye Usage",

    // Smart Image
    totalSmartImageAskQuestionCount: "Questions Asked",
    smartImageAskQuestionCount: "Questions Asked",
    totalSmartImageAnswerCount: "Answers Received",
    smartImageAnswerCount: "Answers Received",
    totalSmartImageCount: "Smart Image Usage",
    smartImageCount: "Smart Image Usage",

    // Feature Usage
    totalFeatureUsage: "Total Feature Usage",

    // Document
    totalDocumentPageCount: "Document Pages",
    readerPages: "Reader Pages",
    readerPagesCount: "Reader Pages",
    accessiblePagesCount: "Accessible Pages",
    totalDocumentUploadedCount: "Documents Uploaded",
    totalDocumentDeletedCount: "Documents Deleted",
    totalDocumentDeletedPages: "Pages Deleted",
    totalDocumentReadCount: "Documents Read",
    totalDocumentSharedCount: "Documents Shared",
    totalDocumentSummaryCount: "Summaries Generated",
    totalDocumentGenQuestionCount: "Document General Questions",
    documentGenQuestionCount: "Document General Questions",
    totalDocumentGenAnswerCount: "Document General Answers",
    documentGenAnswerCount: "Document General Answers",
    totalDocumentAddQuestionCount: "Document Questions Asked",
    documentAddQuestionCount: "Document Questions Asked",
    totalDocumentAddAnswerCount: "Document Answers Received",
    documentAddAnswerCount: "Document Answers Received",
    totalDocumentDownloadCount: "Documents Downloaded",
    totalDocumentInteractions: "Document Interactions",

    // Document Reader
    pdfProcessingStarted: "PDF Processing Started",
    firstPageProcessed: "First Page Processed",
    processPerPagePdf: "Per-Page Processing",
    previousPage: "Previous Page Clicks",
    nextPage: "Next Page Clicks",
    pageReadingStarted: "Reading Started",
    pageReadingCompleted: "Reading Completed",
    documentReaderInteraction: "Reader Interactions",

    // Camera Based Features
    cameraBasedFeatures: "Total Camera Features",
    totalObjectsDetected: "Objects Detected",
    objectImagesClicked: "Object Images Clicked",
    currencyUsage: "Currency Usage",
    textModeUsage: "Text Mode Usage",
    smartEyeUsage: "Smart Eye Usage",

    // Non Camera Based Features
    nonCameraBasedFeatures: "Total Non-Camera Features",
    miraUsage: "MIRA Usage",
    smartImageUsage: "Smart Image Usage",
    tutorUsage: "AI Tutor Usage",
    textToAudioUsage: "Text to Audio Usage",
  };

  // Helper function to format keys to human-friendly titles
  const formatKeyToTitle = (key, category) => {
    if (!key || typeof key !== "string") return "";

    if (isOrgAdmin) {
      const customTitles = {
        usersCount: "No. of users",
        sessionsCount: "No. of sessions",
        cameraBasedFeatures: "No. of camera based features",
        nonCameraBasedFeatures: "No. of non-camera based features",
        readerPagesCount: "No. of pages in Reader",
        accessiblePagesCount: "No. of accessible pages",
      };
      if (customTitles[key]) {
        return customTitles[key];
      }
    }

    if (key === "total") {
      if (category === "cameraBasedFeatures") return "Total Camera Features";
      if (category === "nonCameraBasedFeatures") return "Total Non-Camera Features";
      return "Total";
    }

    if (CARD_TITLES[key]) {
      return CARD_TITLES[key];
    }

    let title = key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    if (title.startsWith("Total ") && title !== "Total Users" && title !== "Total Feature Usage") {
      title = title.replace(/^Total\s+/, "");
    }

    return title;
  };

  // Card descriptions / tooltips explaining feature metrics
  const CARD_DESCRIPTIONS = {
    // Document Metrics
    totalDocumentReadCount:
      "Total number of times documents have been read or reading sessions completed by users in Document Reader.",
    totalDocumentInteractions:
      "Total engagements across documents: Document Reads + AI Questions Generated + User Questions Asked + Document Summaries (Q&A + Read).",
    totalDocumentPageCount:
      "Total number of pages processed across all uploaded documents.",
    readerPages:
      "Total pages read by users inside Document Reader.",
    readerPagesCount:
      "Total pages read by users inside Document Reader.",
    accessiblePagesCount:
      "Total accessible document pages available.",
    totalDocumentUploadedCount:
      "Total number of documents uploaded.",
    totalDocumentDeletedCount:
      "Total number of documents deleted.",
    totalDocumentDeletedPages:
      "Total number of document pages deleted.",
    totalDocumentSharedCount:
      "Total number of documents shared.",
    totalDocumentSummaryCount:
      "Total document summaries generated or viewed.",
    totalDocumentGenQuestionCount:
      "General questions asked across documents in Doc Gen Q&A.",
    documentGenQuestionCount:
      "General questions asked across documents in Doc Gen Q&A.",
    totalDocumentGenAnswerCount:
      "General answers received in Doc Gen Q&A.",
    documentGenAnswerCount:
      "General answers received in Doc Gen Q&A.",
    totalDocumentAddQuestionCount:
      "User asks a question on a specific document.",
    documentAddQuestionCount:
      "User asks a question on a specific document.",
    totalDocumentAddAnswerCount:
      "The answer returned for that document question.",
    documentAddAnswerCount:
      "The answer returned for that document question.",
    totalDocumentDownloadCount:
      "Total documents downloaded.",

    // Document Reader Metrics
    pdfProcessingStarted:
      "Number of times PDF document processing was started in Document Reader.",
    firstPageProcessed:
      "First page successfully processed count in Document Reader.",
    processPerPagePdf:
      "Per-page PDF processing count in Document Reader.",
    previousPage:
      "Previous page navigation button clicks in Document Reader.",
    nextPage:
      "Next page navigation button clicks in Document Reader.",
    pageReadingStarted:
      "Page reading sessions started count in Document Reader.",
    pageReadingCompleted:
      "Page reading sessions completed count in Document Reader.",
    documentReaderInteraction:
      "Total user interactions inside Document Reader.",

    // Overview & Users Metrics
    usersCount: "Total unique active users in the selected period.",
    sessionsCount: "Total user sessions logged in the selected period.",
    numberOfUsers: "Total unique active users in the selected period.",
    numberOfSessions: "Total user sessions logged in the selected period.",
    totalUsers: "Total unique active users in the selected period.",
    totalSessions: "Total user sessions logged in the selected period.",

    // Camera Based Features
    cameraBasedFeatures:
      "Total usage of camera-based features (Object detection, Currency, Text mode, Smart Eye).",
    totalObjectsDetected:
      "Total number of objects detected across all images.",
    objectImagesClicked:
      "Total number of object images captured.",
    currencyUsage:
      "Total number of currency detection scans.",
    textModeUsage:
      "Total number of times Text Mode was used.",
    smartEyeUsage:
      "Total number of times Smart Eye was used.",

    // Non-Camera Based Features
    nonCameraBasedFeatures:
      "Total usage of non-camera features (MIRA, AI Tutor, Smart Image).",
    miraUsage:
      "Total number of MIRA voice assistant queries.",
    smartImageUsage:
      "Total number of Smart Image interactions.",
    tutorUsage:
      "Total number of AI Tutor questions asked.",
    textToAudioUsage:
      "Total text-to-audio conversions.",
  };

  // Color palette for dynamic cards - mapped by category
  const categoryColorMap = {
    overview: "primary",
    cameraBasedFeatures: "warning",
    nonCameraBasedFeatures: "info",
    document: "secondary",
  };

  // Icon palette for dynamic cards - mapped by category
  const categoryIconMap = {
    overview: <Person />,
    cameraBasedFeatures: <CameraEnhance />,
    nonCameraBasedFeatures: <Assistant />,
    document: <AutoStories />,
  };

  // Generate cards dynamically from nested graph2Data structure
  const allCardsConfig = useMemo(() => {
    if (!graph2Data || typeof graph2Data !== "object") return [];

    const cards = [];

    Object.entries(graph2Data).forEach(([category, categoryData]) => {
      if (categoryData && typeof categoryData === "object") {
        Object.keys(categoryData).forEach((key) => {
          cards.push({
            id: `${category}.${key}`,
            title: formatKeyToTitle(key, category),
            description: CARD_DESCRIPTIONS[key] || "",
            category,
            dataKey: key,
            icon: categoryIconMap[category] || <SearchOutlined />,
            color: categoryColorMap[category] || "primary",
          });
        });
      }
    });

    return cards;
  }, [graph2Data]);

  // Get unique categories for grouping in the selection dialog
  const categories = useMemo(() => {
    return [...new Set(allCardsConfig.map((card) => card.category))];
  }, [allCardsConfig]);

  // Load selected cards from localStorage or use all available cards
  const [selectedCards, setSelectedCards] = useState([]);

  // Store card indexes - maps cardId to { configId, index }
  const [cardIndexMap, setCardIndexMap] = useState({});

  // Build gaConfigsMap when GA configs are loaded
  useEffect(() => {
    if (isGaConfigsSuccess && gaConfigsData?.data) {
      const configMap = {};
      gaConfigsData.data.forEach((config) => {
        configMap[config.keyName] = config;
      });
      setGaConfigsMap(configMap);
    }
  }, [isGaConfigsSuccess, gaConfigsData]);

  // Update selected cards when allCardsConfig changes (data loads)
  useEffect(() => {
    if (allCardsConfig.length > 0 && !hasInitializedCards.current) {
      // Check for saved cards from API (gaConfigsData) - cards with valid index
      const savedCardsFromApi = gaConfigsData?.data?.filter(
        (config) => config.index !== null && config.index !== undefined,
      );

      if (
        savedCardsFromApi &&
        savedCardsFromApi.length > 0 &&
        Object.keys(gaConfigsMap).length > 0
      ) {
        // Sort saved cards by index and map to card IDs
        const sortedSavedCards = savedCardsFromApi
          .sort((a, b) => a.index - b.index)
          .map((config) => {
            // Find the card in allCardsConfig that matches this keyName
            const matchingCard = allCardsConfig.find(
              (card) => card.dataKey === config.keyName,
            );
            return matchingCard?.id;
          })
          .filter(Boolean); // Remove any undefined values

        if (sortedSavedCards.length > 0) {
          setSelectedCards(sortedSavedCards.slice(0, MAX_CARDS));
          hasInitializedCards.current = true;
          return;
        }
      }

      // Fallback to localStorage if no saved cards from API
      const saved = localStorage.getItem("dashboardSelectedCards");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Filter to only include valid card IDs that exist in current config
          const validCards = parsed.filter(
            (id) =>
              !id.startsWith("usageThresholds") &&
              allCardsConfig.some((card) => card.id === id),
          );
          if (validCards.length > 0) {
            setSelectedCards(validCards.slice(0, MAX_CARDS));
            hasInitializedCards.current = true;
            return;
          }
        } catch {
          // Fall through to default
        }
      }

      // Default: select first MAX_CARDS when no saved cards exist
      setSelectedCards(
        allCardsConfig.slice(0, MAX_CARDS).map((card) => card.id),
      );
      hasInitializedCards.current = true;
    }
  }, [allCardsConfig, gaConfigsMap, gaConfigsData]);

  // Initialize cardIndexMap when selectedCards and gaConfigsMap are ready
  useEffect(() => {
    if (selectedCards.length > 0 && Object.keys(gaConfigsMap).length > 0) {
      const indexMap = {};
      selectedCards.forEach((cardId, idx) => {
        const card = allCardsConfig.find((c) => c.id === cardId);
        const keyName = card?.dataKey;
        const config = gaConfigsMap[keyName];
        if (config?.id) {
          indexMap[cardId] = {
            configId: config.id,
            index: idx,
          };
        }
      });
      setCardIndexMap(indexMap);
    }
  }, [selectedCards, gaConfigsMap, allCardsConfig]);

  // Temporary selection state for dialog
  const [tempSelectedCards, setTempSelectedCards] = useState([]);

  // Generate payload for saving card order - split cardId by "." and use second part to match with gaConfigsMap
  const generatePayload = () => {
    const payload = selectedCards.map((cardId, idx) => {
      // Split by "." and get the second part (e.g., "overview.totalSessions" -> "totalSessions")
      const keyName = cardId.includes(".") ? cardId.split(".")[1] : cardId;
      const config = gaConfigsMap[keyName];
      return {
        id: config?.id,
        index: idx,
      };
    });

    // Filter out items without valid id
    return payload.filter((item) => item.id);
  };

  // Save selected cards to localStorage when changed
  useEffect(() => {
    localStorage.setItem(
      "dashboardSelectedCards",
      JSON.stringify(selectedCards),
    );
  }, [selectedCards]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle drag end for reordering cards
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSelectedCards((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);

        // Update cardIndexMap with new indexes for all cards
        setCardIndexMap((prevMap) => {
          const updatedMap = { ...prevMap };
          newOrder.forEach((cardId, idx) => {
            if (updatedMap[cardId]) {
              updatedMap[cardId] = {
                ...updatedMap[cardId],
                index: idx,
              };
            }
          });
          return updatedMap;
        });

        // Mark as changed
        setHasCardChanges(true);

        return newOrder;
      });
    }
  };

  const handleCardToggle = (cardId) => {
    setTempSelectedCards((prev) => {
      if (prev.includes(cardId)) {
        return prev.filter((id) => id !== cardId);
      }
      if (prev.length >= MAX_CARDS) {
        return prev;
      }
      return [...prev, cardId];
    });
  };

  const handleSaveCardSettings = () => {
    setSelectedCards(tempSelectedCards);

    // Update cardIndexMap with new card selection and indexes
    const updatedMap = {};
    tempSelectedCards.forEach((cardId, idx) => {
      const card = allCardsConfig.find((c) => c.id === cardId);
      const keyName = card?.dataKey;
      const config = gaConfigsMap[keyName];
      if (config?.id) {
        updatedMap[cardId] = {
          configId: config.id,
          index: idx,
        };
      }
    });
    setCardIndexMap(updatedMap);

    // Mark as changed
    setHasCardChanges(true);

    setCardSettingsOpen(false);
  };

  // Handle save button click - calls API to save card order
  const handleSaveCardOrder = async () => {
    if (isOrgAdmin) {
      localStorage.setItem(
        "dashboardSelectedCards",
        JSON.stringify(selectedCards),
      );
    }

    const payload = generatePayload();
    if (payload.length === 0) {
      setHasCardChanges(false);
      return;
    }

    try {
      await bulkUpdateIndex({ items: payload }).unwrap();
      // Reset the change tracking on success
      setHasCardChanges(false);
    } catch (error) {
      // Handle error silently
    }
  };

  const handleOpenCardSettings = () => {
    setTempSelectedCards(selectedCards);
    setCardSettingsOpen(true);
  };

  // Get card value from data - search for the key in all categories
  const getCardValue = (card) => {
    const key = card.dataKey;
    const foundCategory = Object.keys(graph2Data || {}).find(
      (category) =>
        graph2Data[category] &&
        typeof graph2Data[category] === "object" &&
        key in graph2Data[category],
    );
    return foundCategory ? graph2Data[foundCategory][key] ?? 0 : 0;
  };

  const {
    isLoading: isLoadingOrgs,
    isSuccess: isSuccessOrgs,
    isError: isErrorOrgs,
    data: organizationsData,
  } = useGetOrganizationsQuery(undefined, {
    skip: !ability.can("get", "Organizations"),
  });

  const organizationId = watch("organizationId");

  const {
    data: graphData,
    isSuccess,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetGoogleAnalyticsQuery(
    { refresh, ...dashboardFilter },
    {
      skip: custom || isOrgAdmin,
    },
  );

  const {
    data: graphDataByRange,
    isSuccess: isSuccessByRange,
    isLoading: isLoadingByRange,
    isError: isErrorByRange,
    error: errorByRange,
    isFetching: isFetchingByRange,
    // refetch,
  } = useGetGoogleAnalyticsByRangeQuery(
    {
      startDate: customStartDate,
      endDate: customEndDate,
      ...dashboardFilter,
    },
    {
      skip: !custom || isOrgAdmin,
    },
  );

  const {
    data: orgAnalyticsData,
    isSuccess: isOrgSuccess,
    isLoading: isOrgLoading,
    isFetching: isOrgFetching,
    isError: isOrgError,
    error: orgError,
  } = useGetOrganizationAnalyticsQuery(
    {
      organizationId: user?.organizationId || (typeof localStorage !== "undefined" && localStorage ? localStorage.getItem("orgId") : null),
      duration,
      eventPlace: selectedEventPlace,
      tag: watch("tag"),
      startDate: custom ? customStartDate : undefined,
      endDate: custom ? customEndDate : undefined,
      userType: watch("userType"),
      usageCohort: watch("usageCohort") || "all",
    },
    {
      skip: !isOrgAdmin,
    }
  );

  const {
    data: eventPlaces,
    isSuccess: isSuccessEventPlaces,
    isLoading: isLoadingEventPlaces,
  } = useGetEventPlacesQuery({
    organizationId: watch("organizationId"),
  });



  const userEventPlaceMap = {
    all: "allEventPlaces",
    device: "deviceEventPlaces",
    subscription: "subscriptionEventPlaces",
    free: "allEventPlaces",
    vosapSubscription: "subscriptionEventPlaces",
    under50: "allEventPlaces",
    "50Plus": "allEventPlaces",
    "150Plus": "allEventPlaces",
  };

  const handleEventPlaceChange = (event) => {
    try {
      const selectedPlace = event.target.value;
      setSelectedEventPlace(selectedPlace);
      setValue("eventPlace", selectedPlace);

      const userTypeValue = watch("userType");
      const mapKey = userEventPlaceMap[userTypeValue] || "allEventPlaces";
      const eventPlacesForUserType = eventPlaces?.data?.[mapKey] || [];

      const selectedTags =
        eventPlacesForUserType.filter(
          (place) => place.eventPlace === selectedPlace && place.tags !== null,
        )[0]?.tags || [];

      setFilteredTags(selectedTags);
      // Reset the tag field
      setValue("tag", "");
    } catch (error) {
      console.error("Error while handling event place change:", error);
    }
  };

  useEffect(() => {
    setValue("organizationId", dashboardFilter?.organizationId || "");
    setValue("distributorId", dashboardFilter?.distributorId || "");
    setValue("eventPlace", dashboardFilter?.eventPlace || "");
    setSelectedEventPlace(dashboardFilter?.eventPlace || "");
    setValue("tag", dashboardFilter?.tag || "");
    setValue("deviceCreationDates", dashboardFilter?.deviceCreationDates || []);
    setValue("cameraType", dashboardFilter?.cameraType || "");
    setValue("userType", dashboardFilter?.userType || (isOrgAdmin ? "vosapSubscription" : "all"));
    setValue("usageCohort", dashboardFilter?.usageCohort || "all");
  }, [dashboardFilter, setValue, isOrgAdmin]);

  useEffect(() => {
    if (!custom && isSuccess && graphData?.success) {
      setGraphAllData(graphData?.data);
      setGraph1Data(graphData?.data?.graph1?.[duration]);
      setGraph2Data(graphData?.data?.graph2?.[duration]);
      setGraph3Data(graphData?.data?.graph3?.[duration]);
      setGraph4Data(graphData?.data?.graph4);
      setGraph5Data(graphData?.data?.graph5?.[duration]);
    } else if (custom && isSuccessByRange && graphDataByRange?.success) {
      setGraphAllData(graphDataByRange?.data);
      setGraph1Data(graphDataByRange?.data?.graph1);
      setGraph2Data(graphDataByRange?.data?.graph2);
      setGraph3Data(graphDataByRange?.data?.graph3);
      setGraph4Data(graphDataByRange?.data?.graph4);
      setGraph5Data(graphDataByRange?.data?.graph5);
    }
  }, [graphData, duration, isSuccess, custom, graphDataByRange, isSuccessByRange]);

  useEffect(() => {
    if (isOrgAdmin) {
      const dataSrc = (isOrgSuccess && orgAnalyticsData?.success && orgAnalyticsData?.data) ? orgAnalyticsData?.data : null;
      if (dataSrc) {
        const hasGraph2 = dataSrc && (dataSrc.graph2 || dataSrc.totalUsers !== undefined);
        const currentMetrics = hasGraph2
          ? (dataSrc?.graph2?.[duration === "threeMonth" ? "threeMonth" : duration] || dataSrc?.graph2?.weekly)
          : dataSrc;

        const rawUsersCount = currentMetrics?.overview?.totalUsers ?? dataSrc?.totalUsers ?? dataSrc?.usersCount ?? dataSrc?.users ?? 0;
        const sessionsCountVal = currentMetrics?.overview?.totalEvents ?? dataSrc?.sessionsCount ?? dataSrc?.totalSessions ?? dataSrc?.sessions ?? 0;
        
        const cameraBasedFeaturesVal = currentMetrics?.cameraBasedFeatures?.total ?? dataSrc?.cameraBasedFeaturesCount ?? dataSrc?.cameraFeaturesCount ?? dataSrc?.cameraBasedFeatures?.total ?? 0;
        const nonCameraBasedFeaturesVal = currentMetrics?.nonCameraBasedFeatures?.total ?? dataSrc?.nonCameraBasedFeaturesCount ?? dataSrc?.nonCameraFeaturesCount ?? dataSrc?.nonCameraBasedFeatures?.total ?? 0;
        
        const readerPagesCountVal = currentMetrics?.document?.readerPages ?? dataSrc?.readerPagesCount ?? dataSrc?.pagesInReader ?? dataSrc?.readerPages ?? 0;
        const accessiblePagesCountVal = currentMetrics?.document?.accessiblePages ?? dataSrc?.accessiblePagesCount ?? dataSrc?.accessiblePages ?? 0;

        setGraphAllData(dataSrc);
        
        const g1 = custom ? dataSrc?.graph1 : dataSrc?.graph1?.[duration === "threeMonth" ? "threeMonth" : duration];
        const g3 = custom ? dataSrc?.graph3 : dataSrc?.graph3?.[duration === "threeMonth" ? "threeMonth" : duration];
        const g4 = dataSrc?.graph4;
        const g5 = custom ? dataSrc?.graph5 : dataSrc?.graph5?.[duration === "threeMonth" ? "threeMonth" : duration];

        setGraph1Data(g1 || []);
        setGraph3Data(g3 || []);
        setGraph4Data(g4 || []);
        setGraph5Data(g5 || []);

        const overviewData = {
          usersCount: rawUsersCount,
          sessionsCount: sessionsCountVal,
        };

        setGraph2Data({
          overview: overviewData,
          cameraBasedFeatures: {
            cameraBasedFeatures: cameraBasedFeaturesVal,
          },
          nonCameraBasedFeatures: {
            nonCameraBasedFeatures: nonCameraBasedFeaturesVal,
          },
          document: {
            readerPagesCount: readerPagesCountVal,
            accessiblePagesCount: accessiblePagesCountVal,
          },
        });
      }
    }
  }, [isOrgAdmin, isOrgSuccess, orgAnalyticsData, duration, custom]);
  // Card layout initialization for organization admin is handled in the main useEffect block above.

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (date && date.isValid()) {
      setCustomStartDate(date.format("YYYY-MM-DD"));
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (date && date.isValid()) {
      setCustomEndDate(date.format("YYYY-MM-DD"));
    }
  };

  const fetchData = () => {
    setRefresh((prev) => prev + 1);
  };

  const onSubmit = (data) => {
    dispatch(
      setDashboardFilters({
        ...data,
        eventPlace: selectedEventPlace,
      }),
    );

    if (startDate && startDate.isValid()) {
      setCustomStartDate(startDate.format("YYYY-MM-DD"));
    }
    if (endDate && endDate.isValid()) {
      setCustomEndDate(endDate.format("YYYY-MM-DD"));
    }
  };

  const handleResetFilters = () => {
    dispatch(resetDashboardFilters());
    reset({
      organizationId: "",
      distributorId: "",
      eventPlace: "",
      tag: "",
      deviceCreationDates: [],
      cameraType: "",
      userType: isOrgAdmin ? "vosapSubscription" : "all",
      usageCohort: "all",
    });
    setSelectedEventPlace("");
    setFilteredTags([]);
    setCustomStartDate(startDate?.format("YYYY-MM-DD"));
    setCustomEndDate(endDate?.format("YYYY-MM-DD"));
  };

  const userType = watch("userType");

  // Condition to hide organization dropdown if userType is "free"
  const shouldHideOrganization = userType === "free";

  // Condition to remove "Free Users" from userType dropdown if an organization is selected
  const shouldShowFreeUsers = organizationId === ""; // Show Free Users only when no organization is selected
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Helmet>
        <title> Dashboard </title>
      </Helmet>

      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            marginBottom: 5,
            gap: 2,
          }}
        >
          <Typography variant="h4">Hi, Welcome back</Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Button
              variant={
                !custom && duration === "weekly" ? "contained" : "outlined"
              }
              type="button"
              id="weekly"
              onClick={() => {
                setDuration("weekly");
                setCustom(false);
              }}
              size="small"
            >
              1W
            </Button>
            <Button
              variant={
                !custom && duration === "monthly" ? "contained" : "outlined"
              }
              type="button"
              id="monthly"
              onClick={() => {
                setDuration("monthly");
                setCustom(false);
              }}
              size="small"
            >
              1M
            </Button>
            <Button
              variant={
                !custom && duration === "threeMonth" ? "contained" : "outlined"
              }
              type="button"
              id="threeMonth"
              onClick={() => {
                setDuration("threeMonth");
                setCustom(false);
              }}
              size="small"
            >
              3M
            </Button>
            <Button
              variant={
                !custom && duration === "yearly" ? "contained" : "outlined"
              }
              type="button"
              id="yearly"
              onClick={() => {
                setDuration("yearly");
                setCustom(false);
              }}
              size="small"
            >
              1Y
            </Button>
            <Button
              variant={custom ? "contained" : "outlined"}
              type="button"
              id="all"
              onClick={() => {
                setDuration("weekly");
                setCustom(true);
              }}
              size="small"
            >
              Custom
            </Button>
          </Box>
        </Box>

        {custom && (
          <>
            <Grid
              container
              sx={{
                marginBottom: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              md={6}
              spacing={3}
            >
              <Grid item md={6}>
                <DatePicker
                  maxDate={dayjs(endDate).subtract(6, "day")}
                  // {...register("startDate")}
                  label="Start Date"
                  format="DD/MM/YYYY"
                  disableFuture
                  value={startDate}
                  onChange={handleStartDateChange}
                  // renderInput={(params) => <TextField {...params} />}
                />
              </Grid>
              <Grid item md={6}>
                <DatePicker
                  minDate={dayjs(startDate).add(6, "day")}
                  // {...register("endDate")}
                  label="End Date"
                  format="DD/MM/YYYY"
                  disableFuture
                  value={endDate}
                  onChange={handleEndDateChange}
                  // renderInput={(params) => <TextField {...params} />}
                />
              </Grid>
            </Grid>

            <Divider
              sx={{
                marginBottom: 2,
              }}
            />
          </>
        )}

        <form>
          <Grid container spacing={2} mb={2}>
            {!isOrgAdmin && !shouldHideOrganization && ability.can("get", "Organizations") && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="organization-label">
                    Select Organization
                  </InputLabel>
                  <Select
                    labelId="organization-label"
                    id="demo-simple-select"
                    label="Select Organization"
                    value={organizationId ?? ""}
                    inputProps={{
                      "data-testid": "organizationId",
                    }}
                    onChange={(e) => {
                      setValue("organizationId", e.target.value);
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {!isLoadingOrgs &&
                      isSuccessOrgs &&
                      !isErrorOrgs &&
                      organizationsData?.data
                        ?.toSorted((a, b) => a?.name?.localeCompare(b?.name))
                        ?.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.name}
                          </MenuItem>
                        ))}
                    {isLoadingOrgs && (
                      <MenuItem>
                        <CircularProgress />
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
            )}


            {/* UserType Organization */}
            {ability.can("Is", "organization") && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="userType-label">User Type</InputLabel>
                  <Select
                    labelId="userType-label"
                    id="demo-simple-select"
                    label="User Type"
                    value={watch("userType") || (isOrgAdmin ? "vosapSubscription" : "all")}
                    onChange={(e) => setValue("userType", e.target.value)}
                    inputProps={{ "data-testid": "userType" }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="vosapSubscription">VOSAP Subscription Users</MenuItem>
                    <MenuItem value="device">Device Users</MenuItem>
                    <MenuItem value="subscription">Premium Users</MenuItem>
                    <MenuItem value="under50">Under 50 Usage Users</MenuItem>
                    <MenuItem value="50Plus">50-150 Usage Users</MenuItem>
                    <MenuItem value="150Plus">150+ Usage Users</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}



            {ability.can("get", "All-Event-Place") && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="eventPlace-label">Event Place</InputLabel>
                  <Select
                    labelId="eventPlace-label"
                    id="demo-simple-select"
                    label="Event Place"
                    value={selectedEventPlace}
                    onChange={handleEventPlaceChange}
                    inputProps={{ "data-testid": "eventPlace" }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {isLoadingEventPlaces && (
                      <MenuItem>
                        <CircularProgress size={24} />
                      </MenuItem>
                    )}

                    {isSuccessEventPlaces &&
                      (() => {
                        let eventPlacesList;
                        switch (userType) {
                          case "device":
                          case "device_free":
                            eventPlacesList =
                              eventPlaces?.data?.deviceEventPlaces;
                            break;
                          case "subscription":
                          case "vosapSubscription":
                          case "device_subscription":
                          case "free_subscription":
                            eventPlacesList =
                              eventPlaces?.data?.subscriptionEventPlaces;
                            break;
                          default:
                            eventPlacesList = eventPlaces?.data?.allEventPlaces;
                            break;
                        }
                        return eventPlacesList
                          ?.filter((data) => data?.eventPlace && typeof data.eventPlace === "string" && data.eventPlace.trim() !== "")
                          ?.map((data, index) => (
                            <MenuItem
                              key={`${data.eventPlace}-${index}`}
                              value={data.eventPlace}
                            >
                              {data.eventPlace}
                            </MenuItem>
                          ));
                      })()}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="tag-label">Tag</InputLabel>
                <Select
                  labelId="tag-label"
                  id="tag-select"
                  label="Tag"
                  value={watch("tag") ?? ""}
                  onChange={(e) => setValue("tag", e.target.value)}
                  disabled={filteredTags.length === 0}
                  inputProps={{ "data-testid": "tag" }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {filteredTags.length > 0 ? (
                    filteredTags.map((tag, index) => (
                      <MenuItem key={index} value={tag}>
                        {tag}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No tags available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {(ability.can("Is", "admin") || ability.can("Is", "support")) && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="cameraType-label">Camera Type</InputLabel>
                  <Select
                    labelId="cameraType-label"
                    id="demo-simple-select"
                    label="Camera Type"
                    value={watch("cameraType") ?? ""}
                    onChange={(e) => setValue("cameraType", e.target.value)}
                    inputProps={{ "data-testid": "cameraType" }}
                  >
                    <MenuItem value="all">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="HARDWARE">Mobile</MenuItem>
                    <MenuItem value="USB">Smarton Device</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {(ability.can("Is", "admin") || ability.can("Is", "support")) && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="userType-label">User Type</InputLabel>
                  <Select
                    labelId="userType-label"
                    id="demo-simple-select"
                    label="User Type"
                    value={userType ?? "all"}
                    onChange={(e) => setValue("userType", e.target.value)}
                    inputProps={{ "data-testid": "userType" }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {shouldShowFreeUsers && (
                      <MenuItem value="free">Free Users</MenuItem>
                    )}
                    <MenuItem value="subscription">Premium Users</MenuItem>
                    <MenuItem value="device">Device Users</MenuItem>
                    <MenuItem value="vosapSubscription">VOSAP Subscription Users</MenuItem>
                    <MenuItem value="under50">Under 50 Usage Users</MenuItem>
                    <MenuItem value="50Plus">50-150 Usage Users</MenuItem>
                    <MenuItem value="150Plus">150+ Usage Users</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          <Box
            sx={{
              marginBottom: 2,
              gap: 2,
              display: "flex",
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
            >
              Submit
            </LoadingButton>
            <Button
              variant="outlined"
              color="error"
              onClick={handleResetFilters}
            >
              Reset
            </Button>
          </Box>
        </form>

        <Grid
          container
          spacing={3}
          sx={{
            display: "flex",
            alignItems: "stretch",
          }}
          data-testid="dashboard-app-page"
        >
          {(isFetching || isLoading || isLoadingByRange || isFetchingByRange || (isOrgAdmin && (isOrgLoading || isOrgFetching))) && (
            <Grid
              item
              xs={12}
              md={12}
              lg={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress data-testid="graph-loading" />
            </Grid>
          )}

          {isError && (
            <Grid
              item
              xs={12}
              md={12}
              lg={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" color="error">
                No Data Found, Please Try Again
              </Typography>
            </Grid>
          )}

          {isErrorByRange && (
            <Grid
              item
              xs={12}
              md={12}
              lg={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h4" color="error">
                {errorByRange.error}
              </Typography>
            </Grid>
          )}

          {!isError && !isErrorByRange && (
            <>
              {/* Save Order Button - visible when cards are rearranged */}
              {(ability.can("Is", "admin") || ability.can("Is", "support")) && hasCardChanges && (
                <Grid container lg={12} sx={{ marginTop: 2, marginBottom: 1 }}>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={1}
                  >
                    <LoadingButton
                      variant="contained"
                      color="success"
                      onClick={handleSaveCardOrder}
                      size="small"
                      loading={isSavingOrder}
                    >
                      Save Order
                    </LoadingButton>
                  </Grid>
                </Grid>
              )}

              {/* Card Settings Button - Admin, Organization, and Support */}
              {(ability.can("Is", "admin") || isOrgAdmin || ability.can("Is", "support")) && (
                <Grid container lg={12} sx={{ marginTop: 2, marginBottom: 1 }}>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={1}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<Settings />}
                      onClick={handleOpenCardSettings}
                      size="small"
                    >
                      Customize Cards ({selectedCards.length})
                    </Button>
                  </Grid>
                </Grid>
              )}

              {/* Dynamic Cards Grid with Drag and Drop */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={selectedCards}
                  strategy={rectSortingStrategy}
                >
                  <Grid container lg={12} sx={{ marginTop: 1 }} spacing={1}>
                    {selectedCards.map((cardId) => {
                      const card = allCardsConfig.find((c) => c.id === cardId);
                      if (!card) return null;
                      const value = getCardValue(card);
                      return (
                        <SortableCard
                          key={card.id}
                          id={card.id}
                          card={card}
                          value={value}
                        />
                      );
                    })}
                  </Grid>
                </SortableContext>
              </DndContext>

              {/* Card Settings Dialog */}
              <Dialog
                open={cardSettingsOpen}
                onClose={() => setCardSettingsOpen(false)}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle>
                  Select Dashboard Cards
                </DialogTitle>
                <DialogContent>
                  {tempSelectedCards.length >= MAX_CARDS && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Deselect a card to add another.
                    </Alert>
                  )}
                  {categories.map((category) => (
                    <Box key={category} sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "bold",
                          mb: 1,
                          color: `${categoryColorMap[category]}.main`,
                          textTransform: "capitalize",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {categoryIconMap[category]} {formatKeyToTitle(category)}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      <Grid container spacing={1}>
                        {allCardsConfig
                          .filter((card) => card.category === category)
                          .map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={tempSelectedCards.includes(
                                      card.id,
                                    )}
                                    onChange={() => handleCardToggle(card.id)}
                                    disabled={
                                      !tempSelectedCards.includes(card.id) &&
                                      tempSelectedCards.length >= MAX_CARDS
                                    }
                                  />
                                }
                                label={
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <span>{formatKeyToTitle(card.dataKey, card.category)}</span>
                                    {card.description && (
                                      <Tooltip title={card.description} arrow placement="top">
                                        <Box
                                          component="span"
                                          sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            color: "text.disabled",
                                            "&:hover": { color: "text.primary" },
                                          }}
                                        >
                                          <InfoOutlined sx={{ fontSize: 14 }} />
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Box>
                                }
                                sx={{
                                  width: "100%",
                                  m: 0,
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: tempSelectedCards.includes(card.id)
                                    ? "action.selected"
                                    : "transparent",
                                  "&:hover": {
                                    bgcolor: "action.hover",
                                  },
                                }}
                              />
                            </Grid>
                          ))}
                      </Grid>
                    </Box>
                  ))}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setCardSettingsOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveCardSettings}
                    variant="contained"
                    disabled={tempSelectedCards.length === 0}
                  >
                    Save ({tempSelectedCards.length} selected)
                  </Button>
                </DialogActions>
              </Dialog>

              {processedGraph1Data?.length > 0 && (
                <Grid
                  item
                  xs={12}
                  md={8}
                  lg={8}
                  sx={{
                    height: "100%",
                  }}
                >
                  <UsageChart
                    chartData={processedGraph1Data}
                    duration={duration}
                    selectedMetrics={selectedCards}
                  />
                </Grid>
              )}

              {graph3Data &&
                (Array.isArray(graph3Data)
                  ? graph3Data.length > 0
                  : Object.entries(graph3Data)?.length > 0) && (
                  <Grid
                    item
                    xs={12}
                    md={4}
                    lg={4}
                    sx={{
                      height: "100%",
                    }}
                  >
                    <LanguageChart
                      chartData={
                        // For threeMonth, graph3Data is an array of weekly data - aggregate it
                        Array.isArray(graph3Data)
                          ? (() => {
                              const languages = [
                                "Gujarati",
                                "Hindi",
                                "English",
                                "Other",
                              ];
                              return languages.map((lang) => ({
                                label: lang,
                                value: graph3Data.reduce(
                                  (sum, week) =>
                                    sum +
                                    (typeof week?.[lang] === "object" &&
                                    week?.[lang] !== null
                                      ? week?.[lang]?.totalCount ?? 0
                                      : Number(week?.[lang]) || 0),
                                  0,
                                ),
                              }));
                            })()
                          : Object.entries(graph3Data)?.map(([label, val]) => ({
                              label,
                              value:
                                typeof val === "object" && val !== null
                                  ? val?.totalCount ?? 0
                                  : Number(val) || 0,
                            }))
                      }
                      duration={duration}
                    />
                  </Grid>
                )}

              {graph4Data && Object.entries(graph4Data)?.length > 0 && (
                <Grid
                  item
                  xs={12}
                  md={4}
                  lg={4}
                  sx={{
                    height: "100%",
                  }}
                >
                  <SubscribedChart chartData={graph4Data} duration={duration} />
                </Grid>
              )}

              {graph5Data?.length > 0 && (
                <Grid
                  item
                  xs={12}
                  md={8}
                  lg={8}
                  sx={{
                    height: "100%",
                  }}
                >
                  <SignUpUserChart chartData={graph5Data} duration={duration} />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Container>
    </LocalizationProvider>
  );
}